import React from 'react';
import { classNames, If, RootClass } from 'react-extras';
import { AppContext } from 'context/AppContext';
import { Grid, GridRow, GridCol } from 'components/UI/Grid';
import { PrismicLink } from 'components/PrismicLink';
import { LrOverlay } from 'components/LANDR/LrOverlay';
import { LrLogo } from 'components/Icons/LrLogo';
import { homepageLink } from 'helpers';
import { MainNavigationProps, MainNavigationState } from './types';
import { LogoKebab } from './LogoKebab';
import { LogoClose } from './LogoClose';
import SignUpOrGoToApp from './SignUpOrGoToApp';
import LogInLogOut from './LogInLogOut';
import './MainNavigation.scss';

const formatSignupLink = (signupLink: { url: string }, homePage: string | null) =>
    homePage ? { ...signupLink, url: `${signupLink.url}?homePage=${homePage}` } : signupLink;
class MainNavigation extends React.Component<MainNavigationProps, MainNavigationState> {
    navElement: HTMLDivElement;
    navMobileElement: HTMLDivElement;

    constructor(props: MainNavigationProps) {
        super(props);
        this.state = {
            isOpened: false,
            isFixed: false,
            isAnchored: false,
            formatedSignUpLink: formatSignupLink(
                this.props.mainNavigationData.signup_link,
                this.props.homePage,
            ),
        };
    }

    componentDidMount() {
        window.addEventListener('scroll', this.handleScroll);
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.handleScroll);
    }

    handleScroll = () => {
        const scrollOffset = this.navElement.clientHeight || this.navMobileElement.clientHeight;
        const scrollPos = document.documentElement.scrollTop;
        const shouldFix = scrollPos > scrollOffset && !this.state.isFixed;
        const shouldUnfix = scrollPos <= scrollOffset && this.state.isFixed;
        const shouldReset = scrollPos === 0;

        if (shouldReset) {
            return this.resetMenu();
        }
        if (shouldFix) {
            return this.fixMenu();
        }
        if (shouldUnfix) {
            return this.unfixMenu();
        }
    };

    fixMenu = () => this.setState({ isFixed: true, isAnchored: false });

    unfixMenu = () => this.setState({ isFixed: true, isAnchored: true });

    resetMenu = () => this.setState({ isFixed: false, isAnchored: false });

    toggleMobileMenu = () => this.setState({ isOpened: !this.state.isOpened });

    render() {
        const {
            menu_items,
            login_label,
            login_link,
            signup_label,
            go_to_app_label,
            logout_label,
        } = this.props.mainNavigationData;

        const { isOpened, isFixed, isAnchored } = this.state;

        return (
            <AppContext.Consumer>
                {({ isLoggedIn, logOut }) => (
                    <div
                        className={classNames('MainNavigation', {
                            'is-fixed': isFixed,
                            'is-anchored': isAnchored,
                            'is-opened': isOpened,
                        })}
                        ref={ref => (this.navElement = ref || ({} as HTMLDivElement))}
                    >
                        <If condition={isOpened}>
                            <RootClass add="is-mainNavigation-open" />
                        </If>

                        <Grid className="MainNavigation-content">
                            <GridRow
                                horizontalAlign={'h-between'}
                                verticalAlign={'v-center'}
                                className="Header-content"
                            >
                                <GridCol colSize={`2`} offsetSize={`0`}>
                                    <PrismicLink
                                        link={homepageLink(this.props.pageContext.lang)}
                                        className="MainNavigation-logo"
                                    >
                                        <LrLogo />
                                    </PrismicLink>
                                </GridCol>
                                <button
                                    className="MainNavigation-toggle"
                                    onClick={this.toggleMobileMenu}
                                >
                                    <LogoClose />
                                </button>
                                <GridCol
                                    colSize={`10`}
                                    mobileColSize={'12'}
                                    tabletColSize={'12'}
                                    offsetSize={'0'}
                                >
                                    <ul
                                        className="MainNavigation-items"
                                        onClick={this.toggleMobileMenu}
                                    >
                                        {menu_items.map((item, index) => (
                                            <li key={index} className="MainNavigation-item">
                                                <PrismicLink
                                                    label={item.label}
                                                    link={item.link}
                                                    className="MainNavigation-itemButton"
                                                />
                                            </li>
                                        ))}
                                        <li className="MainNavigation-item MainNavigation-item--secondary">
                                            <span className="Sign-up--mobileBtn">
                                                <SignUpOrGoToApp
                                                    label={signup_label}
                                                    link={this.state.formatedSignUpLink}
                                                    goToAppLabel={go_to_app_label}
                                                    className="MainNavigation-itemButton"
                                                    isButton={true}
                                                    isLoggedIn={isLoggedIn}
                                                />
                                            </span>
                                            <LogInLogOut
                                                loginLabel={login_label}
                                                loginLink={login_link}
                                                logoutLabel={logout_label}
                                                isLoggedIn={isLoggedIn}
                                                className="MainNavigation-itemButton"
                                                logOut={logOut}
                                            />
                                        </li>
                                        <li className="MainNavigation-item MainNavigation-item--secondary Sign-up">
                                            <span className="u-hidden-lg-down">
                                                <SignUpOrGoToApp
                                                    label={signup_label}
                                                    link={this.state.formatedSignUpLink}
                                                    goToAppLabel={go_to_app_label}
                                                    className="MainNavigation-itemButton-signUp"
                                                    isButton={true}
                                                    isLoggedIn={isLoggedIn}
                                                />
                                            </span>
                                        </li>
                                    </ul>
                                </GridCol>
                            </GridRow>
                        </Grid>

                        <div
                            className="MainNavigation-mobileContent"
                            ref={ref => (this.navMobileElement = ref || ({} as HTMLDivElement))}
                        >
                            <Grid>
                                <GridRow
                                    horizontalAlign={'h-between'}
                                    verticalAlign={'v-center'}
                                    className="Header-content"
                                >
                                    <GridCol colSize={`2`} mobileColSize={'4'} offsetSize={`0`}>
                                        <PrismicLink
                                            link={homepageLink(this.props.pageContext.lang)}
                                            className="MainNavigation-logo"
                                        >
                                            <LrLogo />
                                        </PrismicLink>
                                    </GridCol>
                                    <GridCol colSize={`10`} mobileColSize={'8'} offsetSize={`0`}>
                                        <ul className="MainNavigation-mobileContent-items">
                                            <li className="MainNavigation-mobileContent-item">
                                                <SignUpOrGoToApp
                                                    label={signup_label}
                                                    link={this.state.formatedSignUpLink}
                                                    goToAppLabel={go_to_app_label}
                                                    className="MainNavigation-itemButton-signUp"
                                                    isButton={true}
                                                    isLoggedIn={isLoggedIn}
                                                />
                                            </li>
                                            <li className="MainNavigation-mobileContent-item">
                                                <button onClick={this.toggleMobileMenu}>
                                                    <LogoKebab />
                                                </button>
                                            </li>
                                        </ul>
                                    </GridCol>
                                </GridRow>
                            </Grid>
                        </div>

                        <div
                            className="MainNavigation-mobileOverlay"
                            onClick={this.toggleMobileMenu}
                        >
                            <LrOverlay />
                        </div>
                    </div>
                )}
            </AppContext.Consumer>
        );
    }
}

export { MainNavigation };
